import React, { useMemo, useState } from "react";

import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Autocomplete,
  Typography,
  Chip,
  Divider,
  Checkbox,
} from "@mui/material";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

import { MaterialReactTable } from "material-react-table";

import { ComparisonTablePlaceholder } from "./TablePlaceholder";
import { mockData } from "../mockData";

const lifecycleOptions = ["Est.", "Engg", "PLM", "AIMS"];

const referenceTypeMap = {
  "Est.": ["piping", "structural", "electrical"],
  Engg: ["Input Data", "2D Drawing", "3D Model"],
  PLM: ["PO #", "MRQ #"],
  AIMS: ["Inspection", "workOrder", "Maintenance", "Data Historian", "Spares"],
};

const auditMetricOptions = [
  "Quantity",
  "Dimension",
  "Cost",
  "Schedule",
  "Weight",
];

const referenceMap = {
  "2D Drawing": ["DRW-1001", "DRW-1002"],
  "3D Model": ["MODEL-01", "MODEL-02"],
  "PO#": ["PO-001", "PO-002", "PO-003"],
  workOrder: ["WO-100", "WO-200"],
  Vendor: ["Vendor A", "Vendor B"],
  BOQ: ["BOQ-01", "BOQ-02"],
  Estimate: ["EST-01"],
  Maintenance: ["MT-01"],
};

const revisionOptions = ["rev-0", "rev-1", "rev-2"];

const metricColors = {
  Quantity: "#e8f5e9",
  Dimension: "#fff3e0",
  Cost: "#e3f2fd",
  Schedule: "#fce4ec",
  Weight: "#f3e5f5",
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const defaultSelection = {
  lifecycle: "",
  referenceType: "",
  reference: "",
  revision: "",
};

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function MSelectionBar() {
  const [leftSelection, setLeftSelection] = useState(defaultSelection);

  const [rightSelection, setRightSelection] = useState(defaultSelection);

  const [search, setSearch] = useState("");

  const [auditMetrics, setAuditMetrics] = useState([]);

  // ─────────────────────────────────────────
  // Ready State
  // ─────────────────────────────────────────

  const comparisonReady =
    leftSelection.lifecycle &&
    leftSelection.referenceType &&
    leftSelection.reference &&
    leftSelection.revision &&
    rightSelection.lifecycle &&
    rightSelection.referenceType &&
    rightSelection.reference &&
    rightSelection.revision;

  // ─────────────────────────────────────────
  // Dynamic Columns
  // ─────────────────────────────────────────

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "semanticId",
        header: "UID",
        size: 180,
        enablePinning: true,
      },

      {
        accessorKey: "component",
        header: "Component",
        size: 250,
      },

      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        muiTableHeadCellProps: {
          align: "center",
        },

        muiTableBodyCellProps: {
          align: "center",
        },

        Cell: ({ cell }) => {
          const value = cell.getValue();

          const statusConfig = {
            Matched: {
              color: "success",
              variant: "filled",
            },

            "Variance Found": {
              color: "warning",
              variant: "filled",
            },

            "Major Variance": {
              color: "error",
              variant: "filled",
            },

            "Critical Variance": {
              color: "error",
              variant: "filled",
            },

            "Missing In A": {
              color: "info",
              variant: "outlined",
            },

            "Missing In B": {
              color: "info",
              variant: "outlined",
            },

            Pending: {
              color: "default",
              variant: "outlined",
            },
          };

          const config = statusConfig[value] || {
            color: "default",
            variant: "outlined",
          };

          return (
            <Chip
              size="small"
              label={value}
              color={config.color}
              variant={config.variant}
              sx={{
                fontWeight: 700,
                minWidth: 120,
              }}
            />
          );
        },
      },
    ];

    const subColumnStyle = ({ metric }) => ({
      grow: false,
      size: 80,
      muiTableHeadCellProps: {
        align: "center",
        sx: {
          bgcolor: metricColors[metric],
        },
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          bgcolor: metricColors[metric],
        },
      },
    });

    const metricColumns = auditMetrics.map((metric) => {
      const leftLabel = `${leftSelection.reference || "A"} (${leftSelection.revision || "-"})`;

      const rightLabel = `${rightSelection.reference || "B"} (${rightSelection.revision || "-"})`;

      return {
        id: metric,

        header: metric,

        muiTableHeadCellProps: {
          sx: {
            bgcolor: metricColors[metric],
            fontWeight: 800,
            textAlign: "center",
            borderLeft: "1px solid",
            borderColor: "divider",
          },
        },

        columns: [
          // ─────────────────────────────
          // Source A
          // ─────────────────────────────
          {
            accessorKey: `${metric}.left`,
            header: leftLabel,

            size: 140,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const value = row.original?.[metric]?.left;

              return (
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {value ?? "-"}
                </Box>
              );
            },
          },

          // ─────────────────────────────
          // Source B
          // ─────────────────────────────
          {
            accessorKey: `${metric}.right`,
            header: rightLabel,

            size: 140,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const value = row.original?.[metric]?.right;

              return (
                <Box
                  sx={{
                    fontWeight: 500,
                  }}
                >
                  {value ?? "-"}
                </Box>
              );
            },
          },

          // ─────────────────────────────
          // Delta
          // ─────────────────────────────
          {
            accessorKey: `${metric}.delta`,
            header: "Δ",

            size: 110,

            ...subColumnStyle({ metric }),

            Cell: ({ row }) => {
              const delta = row.original?.[metric]?.variance;

              // Empty
              if (delta === null || delta === undefined) {
                return "-";
              }

              // Numeric
              if (typeof delta === "number") {
                return (
                  <Box
                    sx={{
                      fontWeight: 700,
                      color:
                        delta > 0
                          ? "success.main"
                          : delta < 0
                            ? "error.main"
                            : "text.secondary",
                    }}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </Box>
                );
              }

              // Text / Status Diff
              return (
                <Chip
                  label={delta}
                  size="small"
                  color={
                    delta === "Matched"
                      ? "success"
                      : delta === "Mismatch" || delta === "Critical"
                        ? "error"
                        : "warning"
                  }
                  variant="outlined"
                />
              );
            },
          },
        ],
      };
    });

    const lastColumns = [
      // {
      //   accessorKey: "remarks",
      //   header: "Remarks",
      //   size: 250,
      // },
    ];
    return [...baseColumns, ...metricColumns, ...lastColumns];
  }, [auditMetrics, leftSelection, rightSelection]);

  // ─────────────────────────────────────────
  // Reusable Selection Section
  // ─────────────────────────────────────────

  const renderSelectionSide = (title, value, setValue) => {
    const referenceTypes = referenceTypeMap[value.lifecycle] || [];

    const references = referenceMap[value.referenceType] || [];

    return (
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CompareArrowsIcon color="primary" fontSize="small" />

            <Typography fontWeight={700}>{title}</Typography>
          </Stack>

          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
          >
            {/* Lifecycle */}
            <TextField
              select
              size="small"
              label="Lifecycle"
              value={value.lifecycle}
              onChange={(e) =>
                setValue({
                  lifecycle: e.target.value,
                  referenceType: "",
                  reference: "",
                  revision: "",
                })
              }
              sx={{ minWidth: 160 }}
            >
              {lifecycleOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>

            {/* Reference Type */}
            <TextField
              select
              size="small"
              label="Reference Type"
              disabled={!value.lifecycle}
              value={value.referenceType}
              onChange={(e) =>
                setValue({
                  ...value,
                  referenceType: e.target.value,
                  reference: "",
                  revision: "",
                })
              }
              sx={{ minWidth: 180 }}
            >
              {referenceTypes.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>

            {/* Reference */}
            <TextField
              select
              size="small"
              label="Reference"
              disabled={!value.referenceType}
              value={value.reference}
              onChange={(e) =>
                setValue({
                  ...value,
                  reference: e.target.value,
                  revision: "",
                })
              }
              sx={{ minWidth: 180 }}
            >
              {references.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>

            {/* Revision */}
            <TextField
              select
              size="small"
              label="Revision"
              disabled={!value.reference}
              value={value.revision}
              onChange={(e) =>
                setValue({
                  ...value,
                  revision: e.target.value,
                })
              }
              sx={{ minWidth: 140 }}
            >
              {revisionOptions.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box>
      {/* ───────────────────────────────────────── */}
      {/* Selection Bar */}
      {/* ───────────────────────────────────────── */}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 4,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={2.5}>
          {/* Two Comparison Sides */}
          <Stack
            direction={{
              xs: "column",
              xl: "row",
            }}
            spacing={2}
          >
            {renderSelectionSide(
              "Comparison Side A",
              leftSelection,
              setLeftSelection,
            )}

            {renderSelectionSide(
              "Comparison Side B",
              rightSelection,
              setRightSelection,
            )}
          </Stack>

          {/* Metrics  */}
          {comparisonReady && (
            <>
              <Divider />

              <Stack
                direction={{
                  xs: "column",
                  lg: "row",
                }}
                spacing={2}
                justifyContent="center"
              >
                {/* Metrics */}
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={auditMetricOptions}
                  value={auditMetrics}
                  onChange={(e, value) => setAuditMetrics(value)}
                  sx={{ minWidth: 500 }}
                  getOptionLabel={(option) => option}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} size="small" />

                      {option}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({
                          index,
                        })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Audit Metrics"
                      placeholder="Select metrics"
                    />
                  )}
                />
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      {/* ───────────────────────────────────────── */}
      {/* Table */}
      {/* ───────────────────────────────────────── */}

      {!comparisonReady || auditMetrics.length === 0 ? (
        <ComparisonTablePlaceholder />
      ) : (
        <MaterialReactTable
          columns={columns}
          data={mockData}
          // enableColumnFilters
          enableColumnActions={false}
          enableSorting={false}
          enableStickyHeader
          enableDensityToggle={false}
          enableFullScreenToggle
          muiTableContainerProps={{
            sx: {
              maxHeight: "65vh",
            },
          }}
          initialState={{
            density: "compact",
            pagination: {
              pageSize: 10,
              pageIndex: 0,
            },
          }}
        />
      )}
    </Box>
  );
}
